import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { CONTRAST, BACKGROUND_PRIMARY } from '../../theme';
import { Dynamic } from '../../reports';

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  height: 42px;
  padding: 12px 12px 0 12px;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 12px 0 12px;
`;

const Close = styled.div`
  color: ${CONTRAST};
  font-size: 36px;
  cursor: pointer;
  line-height: 1.3;

  &:hover {
    opacity: 0.5;
  }
`;
const Panel = styled.div`
  height: calc(100vh - 55px);
  width: ${({ open }) => (open ? '360px' : 0)};
  background-color: white;
  transition: 0.2s width;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border-left: 1px solid ${BACKGROUND_PRIMARY};

  @media (max-width: 768px) {
    width: ${({ open }) => (open ? '100%' : 0)};
    width: 100%;
    height: ${({ open }) => (open ? '60%' : 0)};
    position: absolute;
    bottom: 0;
  }
`;
const ScrollWrapper = styled.div`
  overflow-y: scroll;
`;

const mapState = createSelector(
  ({ sidePanel }) => sidePanel,
  ({ sidePanelContents, selectedSidePanelReport }) => sidePanelContents[selectedSidePanelReport],
  ({ reportLoading }) => reportLoading,
  ({ city }) => city,
  ({ defaultZipcode }) => defaultZipcode,
  ({ state }) => state,
  (
    sidePanel,
    sidePanelContents,
    reportLoading,
    city,
    defaultZipcode,
    state,
  ) => ({
    sidePanel,
    sidePanelContents,
    reportLoading,
    city,
    defaultZipcode,
    state,
  }),
);

export const SidePanel = () => {
  const {
    sidePanel,
    sidePanelContents,
    reportLoading,
    city,
    defaultZipcode,
    state,
  } = useSelector(mapState);
  const { open, type, data } = sidePanel;
  const dispatch = useDispatch();
  const close = useCallback(() => {
    dispatch({ type: 'CLOSE_SIDE_PANEL' });
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      dispatch({ type: 'CLEAR_PARCEL_REPORT' });
    }, 200);
  }, [dispatch]);

  useEffect(() => {
    if (open && type === 'PARCEL') {
      dispatch({
        type: 'FETCH_PARCEL_FOR_REPORT',
        payload: data,
      });
    } else if (open) {
      close();
    }
  }, [open, dispatch, close, data, type]);

  return (
    <Panel open={open}>
      <Row>
        {sidePanelContents ? (
          <Button
            onClick={() => {
              dispatch({
                type: 'SELECT_REPORT',
                payload: sidePanelContents.key,
              });
              dispatch({
                type: 'OPEN_MODAL',
                payload: {
                  type: 'PARCEL_PRELOADED',
                },
              });
            }}
          >
            Full Report
          </Button>
        ) : null}
        <Close onClick={close}>×</Close>
      </Row>
      <ScrollWrapper>
        {(sidePanelContents && open && type === 'PARCEL') || reportLoading ? (
          <Dynamic
            close={close}
            reportContents={sidePanelContents}
            reportLoading={reportLoading}
            city={city}
            defaultZipcode={defaultZipcode}
            state={state}
            limited
          />
        ) : (
          <Column>
            <Typography variant="h6">
              There is no data for those coordinates.
            </Typography>
            <Typography>
              This happens when you click a road or an area outside of your
              region.
{' '}
            </Typography>
          </Column>
        )}
      </ScrollWrapper>
    </Panel>
  );
};
